import { useCallback, useEffect, useRef, useState } from "react";
import userProfile from "../../../src/assets/images/noProfile.png";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import debounce from "lodash/debounce";

import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
  LeftArrow,
  RightArrow,
  deactiveIcon,
  activeIcon,
  EyeShowIcon,
} from "../../../src/assets/icons";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../../content/SidebarContent";
import { routes } from "../../helpers/constants";
import PageHeader from "../../components/PageHeader";
import Modal from "../../components/Modal";
import { useDispatch, useSelector } from "react-redux";
import { IMAGE_URL } from "../../config";
import { notify } from "../../helpers/commonFunctions";
import Loader from "../../components/Loader";
import {
  reqToGetStaffList,
  reqToDeleteStaff,
  reqToUpdateStaff,
} from "../../redux/slices.js/staff/staffSlice";

const StaffList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageRef = useRef(null);
  const { setIsSidebarOpen } = useSidebar();
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeModal, setActiveModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const pageSizes = [10, 20, 30, 50, 100];
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const tabs = ["All", "Active", "Pending", "Deactivated"];
  const [activeTab, setActiveTab] = useState("All");

  // ✅ NEW: Store page number per tab
  const [pageMap, setPageMap] = useState({
    All: 1,
    Active: 1,
    Pending: 1,
    Deactive: 1,
  });

  const staffList = useSelector((state) => state.staff.staffList);
  const loader = useSelector((state) => state.staff.loader);
  const totalCount = useSelector((state) => state.staff.totalCount);

  const currentPage = pageMap[activeTab]; // current page based on activeTab
  const totalPages = Math.ceil(totalCount / pageSize);
  const renderRangeStart = (currentPage - 1) * pageSize + 1;
  const renderRangeEnd = Math.min(currentPage * pageSize, totalCount);

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPageMap((prev) => ({
        ...prev,
        [activeTab]: newPage,
      }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pageRef.current && !pageRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const debouncedSearch = useCallback(
    debounce((val) => {
      setSearch(val);
    }, 500),
    []
  );

  const handleSearchChange = (val) => {
    debouncedSearch(val);
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const fetchStaffList = useCallback(() => {
    const page = pageMap[activeTab] || 1;
    dispatch(
      reqToGetStaffList({
        data: {
          tab: activeTab.toLowerCase(),
          search,
          page,
          limit: pageSize,
        },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [activeTab, pageMap, pageSize, search, dispatch]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  const handleDeleteStaff = () => {
    if (!selectedStaff) return;

    dispatch(
      reqToDeleteStaff({
        id: selectedStaff.id,
        onSuccess: () => {
          setShowModal(false);
          setSelectedStaff(null);
          fetchStaffList(); // Refresh the list after deletion
        },
        onFailure: () => {
          setShowModal(false);
          setSelectedStaff(null);
        },
      })
    );
  };

  const handleActive = () => {
    if (!selectedStaff) return;
    const reqObj = {
      // id: selectedStaff.id,
      action: "active",
    };
    dispatch(
      reqToUpdateStaff({
        id: selectedStaff.id,
        data: reqObj,
        onSuccess: () => {
          setActiveModal(false);
          setSelectedStaff(null);
          fetchStaffList(); // Refresh the list after status change
        },
        onFailure: () => {
          setActiveModal(false);
          setSelectedStaff(null);
        },
      })
    );
  };

  const handleDeactive = () => {
    if (!selectedStaff) return;
    const reqObj = {
      action: "deactive",
    };
    dispatch(
      reqToUpdateStaff({
        id: selectedStaff.id,
        data: reqObj,
        onSuccess: () => {
          setShowModal(false);
          setSelectedStaff(null);
          fetchStaffList(); // Refresh the list after status change
        },
        onFailure: () => {
          setShowModal(false);
          setSelectedStaff(null);
        },
      })
    );
  };

  return (
    <>
      {loader && <Loader />}
      <section className="dashboard-page main-layout-page main-layout h-[calc(100vh-50px)]">
        <div className="!p-0">
          <div className="main-layout-wrapper bg-white h-screen flex flex-col sticky z-1 top-0">
            <PageHeader
              title="Staff List"
              description="Manage your staff members efficiently."
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setSelectedStatus(null);
                setPageMap((prev) => ({ ...prev, [tab]: 1 }));
              }}
              onOpenSidebar={() => setIsSidebarOpen(true)}
              hasSearch={true}
              onSearchChange={handleSearchChange}
            />

            <div className="flex-1 right-sidebar h-full relative">
              <div className="right-sidebar-contend py-6 px-3 md:p-6 pb-[50px] h-full">
                <div className="dashboard-btns flex items-center justify-end gap-4 mb-6">
                  <button
                    className="text-white login-btn w-fit"
                    onClick={() => navigate(routes.addStaff)}
                  >
                    {PlusIcon} Add Staff
                  </button>
                </div>
                <div className="overflow-x-auto rounded-md bg-white box-shadow p-5 w-full common-scroll">
                  <table className="min-w-[650px]">
                    <thead className="bg-white-vlight p-2 h-[45px]">
                      <tr>
                        <th className="p-2 text-sub_span text-blue !font-extrabold text-left w-200">
                          Name
                        </th>
                        <th className="p-2 text-sub_span text-blue !font-extrabold text-left w-250">
                          Email
                        </th>
                        <th className="p-2 text-sub_span text-blue !font-extrabold text-left w-150">
                          Status
                        </th>
                        <th className="p-2 text-sub_span text-blue !font-extrabold text-right w-100">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {staffList?.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            className="text-center py-6 text-gray-500 font-medium"
                          >
                            No staff members found.
                          </td>
                        </tr>
                      ) : (
                        staffList?.map((staff) => (
                          <tr key={staff.id} className="">
                            <td className="py-3 px-2">
                              <div className="flex gap-4 items-center">
                                <img
                                  src={
                                    staff?.profile_picture
                                      ? IMAGE_URL + staff?.profile_picture
                                      : userProfile
                                  }
                                  alt={staff.name}
                                  className="w-[42px] h-[42px] rounded-[10px] object-cover flex-none"
                                />
                                <span className="text-sub_span text-black">
                                  {staff.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="text-sub_span text-black">
                                {staff.email}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div
                                className={`px-4 py-[5px] flex items-center gap-2 w-fit text-sub_span text-black rounded-full !font-semibold ${
                                  staff?.status === "active"
                                    ? "bg-[#00D2611A] text-green-800"
                                    : "bg-[#F03D361A] text-red-600"
                                }`}
                              >
                                <span
                                  className={`w-[10px] h-[10px] rounded-full ${
                                    staff?.status === "active"
                                      ? "bg-[#00D261]"
                                      : "bg-[#F03D36]"
                                  }`}
                                ></span>
                                {staff?.status?.charAt(0).toUpperCase() +
                                  staff?.status?.slice(1)}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-3 justify-end">
                                <button
                                  title="Edit"
                                  className="w-[16px] h-[16px]"
                                  onClick={() =>
                                    navigate(
                                      routes.viewStaff.replace(":id", staff?.id)
                                    )
                                  }
                                >
                                  {EyeShowIcon}
                                </button>
                                <button
                                  title="Edit"
                                  className="w-[16px] h-[16px]"
                                  onClick={() =>
                                    navigate(
                                      routes.editStaff.replace(":id", staff?.id)
                                    )
                                  }
                                >
                                  {EditIcon}
                                </button>
                                {staff?.status !== "active" ? (
                                  <button
                                    title="Activate"
                                    className="w-[16px] h-[16px]"
                                    onClick={() => {
                                      setSelectedStaff(staff);
                                      setActiveModal(true);
                                    }}
                                  >
                                    {deactiveIcon}
                                  </button>
                                ) : (
                                  <button
                                    title="Deactivate"
                                    className="w-[16px] h-[16px]"
                                    onClick={() => {
                                      setSelectedStaff(staff);
                                      setShowModal(true);
                                      setModalType("deactivate");
                                    }}
                                  >
                                    {activeIcon}
                                  </button>
                                )}
                                <button
                                  title="Delete"
                                  className="w-[16px] h-[16px]"
                                  onClick={() => {
                                    setSelectedStaff(staff);
                                    setModalType("delete");
                                    setShowModal(true);
                                  }}
                                >
                                  {DeleteIcon}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {staffList?.length > 0 && totalCount > 0 && (
                  <div className="flex justify-between items-center mt-4 text-sm text-gray-600 flex-wrap gap-3">
                    <div className="text-span !font-medium text-[#8899A8] font-poppins">
                      {renderRangeStart} – {renderRangeEnd} of {totalCount}
                    </div>
                    <div className="flex items-center gap-3 justify-end ml-auto">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => goToPage(page - 1)}
                          disabled={page === 1}
                          className="px-2 py-1 disabled:opacity-40"
                        >
                          {LeftArrow}
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                          <button
                            key={i}
                            onClick={() => goToPage(i + 1)}
                            className={`px-3 py-1 h-[32px] min-w-[32px] !font-normal font-poppins text-sub_span rounded ${
                              page === i + 1
                                ? "bg-blue text-white"
                                : "text-[#4E5D78]"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <button
                          onClick={() => goToPage(page + 1)}
                          disabled={page === totalPages}
                          className="px-2 py-1 disabled:opacity-40"
                        >
                          {RightArrow}
                        </button>
                      </div>
                      <div
                        className="flex items-center justify-center bg-gray-50"
                        ref={pageRef}
                      >
                        <div className="relative inline-block text-left">
                          <button
                            onClick={() => setOpen(!open)}
                            className="flex items-center gap-1 font-poppins !font-normal text-[#637381] text-sub_span bg-white border border-[#DFE4EA] rounded-md px-3 py-1.5 shadow-sm focus:outline-none"
                          >
                            {pageSize}{" "}
                            <span className="font-poppins !font-normal text-[#637381]">
                              / Page
                            </span>
                            <ChevronDownIcon className="w-4 h-4 ml-1 font-poppins !font-normal text-[#637381]" />
                          </button>

                          {open && (
                            <ul className="absolute bottom-full right-0 mt-2 w-28 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                              {pageSizes.map((size) => (
                                <li
                                  key={size}
                                  onClick={() => {
                                    setPageSize(size);
                                    setPage(1);
                                    setOpen(false);
                                  }}
                                  className={`px-4 py-2 font-poppins !font-normal cursor-pointer ${
                                    pageSize === size
                                      ? "font-semibold bg-gray-100"
                                      : ""
                                  }`}
                                >
                                  {size} / Page
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {showModal && modalType === "delete" && (
          <Modal
            show={showModal}
            onClose={() => {
              setShowModal(false);
              setModalType(null);
            }}
            onConfirm={handleDeleteStaff}
            title={`Are you sure you want to delete this staff member?`}
          />
        )}
        {showModal && modalType === "deactivate" && (
          <Modal
            show={showModal}
            onClose={() => {
              setShowModal(false);
              setModalType(null);
            }}
            onConfirm={handleDeactive}
            title={`Are you sure you want to deactivate this staff member?`}
          />
        )}
        {activeModal && (
          <Modal
            show={activeModal}
            onClose={() => setActiveModal(false)}
            onConfirm={handleActive}
            title={`Are you sure you want to activate this staff member?`}
          />
        )}
      </section>
    </>
  );
};

export default StaffList;
